import React, { FC, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import VerificationBadge from 'src/components/elements/verification-badge/VerificationBadge'
import ProfileMenu from './ProfileMenu'
import clsx from 'clsx'
import { useGetUserQuery } from 'src/redux/api'
import { UserIcon } from '@svgs'
import { useTranslation } from 'react-i18next'

interface IUserDropdown {}

const UserDropdown: FC<IUserDropdown> = () => {
  const { t } = useTranslation()
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { data: user } = useGetUserQuery(undefined)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  useEffect(() => {
    const updateBodyOverflow = () => {
      const isMobile = window.matchMedia('(max-width: 991.98px)').matches
      if (showDropdown && isMobile) {
        document.body.classList.add('overflow-hidden')
      } else {
        document.body.classList.remove('overflow-hidden')
      }
    }

    // Initial update
    updateBodyOverflow()

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [showDropdown])

  // The layout guards render this only for authed users, but a refetch can
  // briefly drop the cache — `user.avatar` below must not crash the header.
  if (!user) return null

  return (
    <div className="relative flex flex-col" ref={dropdownRef}>
      <button
        type="button"
        aria-label={t('nav.profile_menu')}
        aria-expanded={showDropdown}
        onClick={(e) => {
          e.preventDefault()
          setShowDropdown(!showDropdown)
        }}
        className={clsx(
          'relative w-11 h-11 shrink-0 bg-brand-soft rounded-full overflow-hidden transition-all duration-300 hover:shadow-lg',
          showDropdown && 'shadow-lg',
        )}
      >
        {user.avatar ? (
          <img className="size-full object-cover" src={user.avatar} alt="" />
        ) : (
          <div className="w-4 h-4 icon-button fill-ink">
            <UserIcon />
          </div>
        )}
      </button>
      {showDropdown && (
        <>
          {/* Mobile: fixed under the app header, clear of the notch, and it
              scrolls when a sideways phone is too low for it. Desktop:
              absolute under the button — `fixed` + top-full would anchor to
              the viewport and open the menu below the screen. */}
          <div className="flex flex-col md:w-[20.25rem] fixed md:absolute top-[calc(var(--header-h)+1rem)] md:top-full max-md:left-[max(1rem,var(--safe-left))] max-md:right-[max(1rem,var(--safe-right))] md:right-0 max-md:max-h-[calc(100%-var(--header-h)-var(--bottom-bar-h)-2rem)] md:mt-2 z-50 overflow-hidden max-md:overflow-y-auto px-6 pt-6 pb-4 bg-surface rounded-2xl shadow-pop">
            <div className="flex flex-col">
              {/* The avatar and name lead to the profile, where the photo can
                  be changed — the menu itself keeps only settings/support. */}
              <Link
                to="/profile"
                onClick={() => setShowDropdown(false)}
                className="flex flex-col self-stretch items-center group"
              >
                {!user.avatar ? (
                  <div className="w-[6.25rem] h-[6.25rem] overflow-hidden rounded-full icon-button fill-ink group-hover:opacity-80 transition-opacity duration-200">
                    <UserIcon />
                  </div>
                ) : (
                  <div className="w-[6.25rem] h-[6.25rem] overflow-hidden rounded-full group-hover:opacity-90 transition-opacity duration-200">
                    <img
                      src={user.avatar}
                      alt={t('profile_picture')}
                      className="object-cover aspect-square size-full"
                    />
                  </div>
                )}
                <div className="flex flex-col items-center mt-4 max-w-full text-ink">
                  <VerificationBadge verified={Boolean(user.diiaVerified)} />
                  <div className="mt-1 text-lg font-semibold text-center break-words max-w-full group-hover:text-brand transition-colors duration-200">
                    {user?.firstName} {user?.lastName}
                  </div>
                  {user?.phone && <div className="mt-1 text-sm">{user.phone}</div>}
                </div>
              </Link>
              <div className="mt-4 w-full bg-ink bg-opacity-10 h-px" />
              <ProfileMenu onNavigate={() => setShowDropdown(false)} />
            </div>
          </div>
          <div
            onClick={() => setShowDropdown(false)}
            className="md:hidden bg-inverse/60 fixed top-[var(--header-h)] inset-x-0 inset-y-0 z-30"
          />
        </>
      )}
    </div>
  )
}

export default UserDropdown
